import { useState } from "react";
import { Plus, Trash2, X, Check, ChevronUp, ChevronDown, Images, Edit2 } from "lucide-react";
import ImageUploadInput from "../ImageUploadInput";

const COLOR_MAP = {
  Yellow: "#FCD34D", White: "#F9FAFB", Pink: "#FCA5A5", Blue: "#93C5FD",
  Green: "#6EE7B7", Red: "#F87171", Black: "#1F2937", Grey: "#9CA3AF",
  Purple: "#C4B5FD", Orange: "#FB923C", Brown: "#92400E",
};

function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const add = (val) => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));

  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const updated = [...tags];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(targetIdx, 0, moved);
    onChange(updated);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="tag-input-container">
      {tags.map((t, i) => (
        <span
          key={t}
          draggable
          onDragStart={() => setDragIdx(i)}
          onDragOver={e => { e.preventDefault(); setOverIdx(i); }}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          onDrop={() => handleDrop(i)}
          className={`tag-item cursor-grab active:cursor-grabbing transition-all select-none ${
            overIdx === i && dragIdx !== i ? "ring-2 ring-orange-400 ring-offset-1 scale-105" : ""
          }`}
          style={{ background: COLOR_MAP[t] ? `${COLOR_MAP[t]}33` : undefined }}
          title="Drag to reorder"
        >
          <span className="text-slate-300 mr-0.5 text-xs">⠿</span>
          {COLOR_MAP[t] && (
            <span className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0"
              style={{ background: COLOR_MAP[t] }} />
          )}
          {t}
          <button type="button" onClick={() => remove(t)}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
          if (e.key === "Backspace" && !input) onChange(tags.slice(0, -1));
        }}
        onBlur={() => { if (input.trim()) add(input); }}
        placeholder={tags.length === 0 ? placeholder : ""}
        className="flex-1 min-w-16 outline-none text-sm text-slate-900 bg-transparent"
      />
    </div>
  );
}

function generateCombinations(variantOptions, existingCombinations = []) {
  if (!variantOptions.length) return [];
  const allValues = variantOptions.map(o => o.values.map(v => ({ name: o.name, value: v })));
  const combinations = allValues.reduce((acc, curr) => {
    if (acc.length === 0) return curr.map(c => [c]);
    const result = [];
    acc.forEach(combo => curr.forEach(c => result.push([...combo, c])));
    return result;
  }, []);

  // Build a lookup from existing combos keyed by variant values so we can preserve data
  const existingMap = {};
  existingCombinations.forEach(combo => {
    const key = variantOptions.map(o => (combo[o.name.toLowerCase()] || "").toLowerCase()).join("|");
    if (key) existingMap[key] = combo;
  });

  return combinations.map(combo => {
    const row = {};
    combo.forEach(({ name, value }) => { row[name.toLowerCase()] = value; });
    const key = variantOptions.map(o => (row[o.name.toLowerCase()] || "").toLowerCase()).join("|");
    const existing = existingMap[key];
    const autoSku = combo.map(({ value }) => value.toUpperCase().slice(0, 3)).join("-");
    if (existing) {
      // Preserve all pricing/inventory data; only overwrite variant dimension values
      return { ...existing, ...row };
    }
    return { ...row, sku: autoSku, price: 0, mrp: 0, stock: 0, weight: 0, image: "", images: [] };
  });
}

/* ── Multi-image editor modal ──────────────────────────────── */
function VariantImagesModal({ combo, variantLabel, onSave, onClose }) {
  // images[] is the source of truth; image is images[0] for backward compat
  const initialImages = (() => {
    const imgs = (combo.images || []).filter(Boolean);
    if (imgs.length > 0) return imgs;
    if (combo.image) return [combo.image];
    return [];
  })();
  const [imgs, setImgs] = useState(initialImages);

  const canAdd = imgs.length < 8;

  const add = () => setImgs(prev => [...prev, ""]);
  const remove = (i) => setImgs(prev => prev.filter((_, idx) => idx !== i));
  const change = (i, val) => setImgs(prev => prev.map((v, idx) => idx === i ? val : v));
  const moveUp = (i) => {
    if (i === 0) return;
    setImgs(prev => { const a = [...prev]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; });
  };
  const moveDown = (i) => {
    setImgs(prev => {
      if (i === prev.length - 1) return prev;
      const a = [...prev]; [a[i], a[i + 1]] = [a[i + 1], a[i]]; return a;
    });
  };

  const handleSave = () => {
    const clean = imgs.filter(Boolean);
    onSave(clean);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="text-sm font-bold text-slate-900">Variant Images</p>
            <p className="text-xs text-slate-500 mt-0.5">{variantLabel} · up to 8 images</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {imgs.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl">
              No images added yet. Click "Add Image" to start.
            </div>
          )}
          {imgs.map((url, i) => (
            <div key={i} className="flex items-start gap-2">
              {/* Reorder */}
              <div className="flex flex-col gap-0.5 pt-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(i)}
                  disabled={i === imgs.length - 1}
                  className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>

              {/* Thumbnail */}
              <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex-shrink-0 overflow-hidden flex items-center justify-center">
                {url ? (
                  <img src={url} alt={`img ${i + 1}`} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                ) : (
                  <Images className="w-4 h-4 text-slate-300" />
                )}
              </div>

              {/* Input */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                    {i === 0 ? "Primary image" : `Image ${i + 1}`}
                  </span>
                </div>
                <ImageUploadInput
                  value={url}
                  onChange={v => change(i, v)}
                  placeholder="Paste URL or upload…"
                />
              </div>

              {/* Remove */}
              <button
                type="button"
                onClick={() => remove(i)}
                className="mt-1 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {canAdd && (
            <button
              type="button"
              onClick={add}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-500 hover:text-orange-600 hover:border-orange-300 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Image ({imgs.length}/8)
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-400">First image is the primary. Reorder with arrows.</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="px-4 py-2 text-sm rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors">
              Save Images
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Compact image cell shown in the table ─────────────────── */
function VariantImagesCell({ combo, mainImage, onEdit }) {
  const imgs = (combo.images || []).filter(Boolean);
  const primaryImg = imgs[0] || combo.image || mainImage || null;
  const count = imgs.length > 0 ? imgs.length : (combo.image ? 1 : 0);

  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      {primaryImg ? (
        <img
          src={primaryImg}
          alt="variant"
          className="w-8 h-8 rounded object-cover border border-slate-200 flex-shrink-0"
          onError={e => { e.target.style.display = "none"; }}
        />
      ) : (
        <div className="w-8 h-8 rounded border border-dashed border-slate-300 bg-slate-50 flex-shrink-0 flex items-center justify-center">
          <Images className="w-3.5 h-3.5 text-slate-300" />
        </div>
      )}
      <div className="min-w-0">
        {count > 0 && (
          <p className="text-[10px] text-slate-400 leading-none mb-0.5">{count} image{count !== 1 ? "s" : ""}</p>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
        >
          <Edit2 className="w-2.5 h-2.5" />
          {count === 0 ? "Add images" : "Edit images"}
        </button>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function VariantsTab({ data, onChange }) {
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [editImagesIdx, setEditImagesIdx] = useState(null);
  const [defaultRow, setDefaultRow] = useState({ sku: "", price: "", mrp: "", stock: "", weight: "" });

  const variantOptions = data.variant_options || [];
  const variantCombinations = data.variant_combinations || [];
  const mainImage = data.main_image || "";

  const addOption = () => {
    const updated = [...variantOptions, { name: "", values: [] }];
    onChange("variant_options", updated);
  };

  const updateOption = (idx, field, value) => {
    const updated = variantOptions.map((o, i) => i === idx ? { ...o, [field]: value } : o);
    onChange("variant_options", updated);
    if (autoGenerate) {
      onChange("variant_combinations", generateCombinations(updated, variantCombinations));
    }
  };

  const removeOption = (idx) => {
    const updated = variantOptions.filter((_, i) => i !== idx);
    onChange("variant_options", updated);
    if (autoGenerate) {
      onChange("variant_combinations", generateCombinations(updated, variantCombinations));
    }
  };

  const moveOptionUp = (idx) => {
    if (idx === 0) return;
    const updated = [...variantOptions];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    onChange("variant_options", updated);
    if (autoGenerate) onChange("variant_combinations", generateCombinations(updated, variantCombinations));
  };

  const moveOptionDown = (idx) => {
    if (idx === variantOptions.length - 1) return;
    const updated = [...variantOptions];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    onChange("variant_options", updated);
    if (autoGenerate) onChange("variant_combinations", generateCombinations(updated, variantCombinations));
  };

  const handleAutoGenerate = (checked) => {
    setAutoGenerate(checked);
    if (checked) {
      onChange("variant_combinations", generateCombinations(variantOptions, variantCombinations));
    }
  };

  const updateCombination = (idx, field, value) => {
    const updated = variantCombinations.map((c, i) => {
      if (i !== idx) return c;
      if (value === undefined) {
        // Remove the field entirely (used for "reset to product default")
        const n = { ...c };
        delete n[field];
        return n;
      }
      return { ...c, [field]: ["price", "mrp", "stock", "weight"].includes(field) ? Number(value) : value };
    });
    onChange("variant_combinations", updated);
  };

  const handleImagesSave = (idx, cleanImages) => {
    const updated = variantCombinations.map((c, i) => {
      if (i !== idx) return c;
      return {
        ...c,
        images: cleanImages,
        image: cleanImages[0] || "",  // keep backward compat
      };
    });
    onChange("variant_combinations", updated);
    setEditImagesIdx(null);
  };

  const removeCombination = (idx) => {
    onChange("variant_combinations", variantCombinations.filter((_, i) => i !== idx));
  };

  const variantKeys = variantOptions.map(o => o.name.toLowerCase()).filter(Boolean);

  // Build variant label for modal header
  const getVariantLabel = (combo) => {
    const parts = variantKeys.map(k => combo[k]).filter(Boolean);
    return parts.length > 0 ? parts.join(" / ") : `Variant ${variantCombinations.indexOf(combo) + 1}`;
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">4</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Variants</h2>
      </div>

      {/* Product Options */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-800">Product Options</h3>
          <button
            type="button"
            onClick={addOption}
            className="flex items-center gap-1.5 text-sm text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Variant Option
          </button>
        </div>

        {variantOptions.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
            No variants added. Click "Add Variant Option" to add Color, Size, etc.
          </div>
        ) : (
          <div className="space-y-4">
            {variantOptions.map((option, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg relative pr-20">
                {/* Reorder buttons */}
                <div className="absolute top-2 right-9 flex flex-col gap-0.5">
                  <button type="button" onClick={() => moveOptionUp(idx)} disabled={idx === 0}
                    title="Move up" className="p-1 rounded hover:bg-slate-200 disabled:opacity-20 transition-colors" data-testid={`option-move-up-${idx}`}>
                    <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                  <button type="button" onClick={() => moveOptionDown(idx)} disabled={idx === variantOptions.length - 1}
                    title="Move down" className="p-1 rounded hover:bg-slateite-200 disabled:opacity-20 transition-colors" data-testid={`option-move-down-${idx}`}>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                </div>
                {/* Delete */}
                <button type="button" onClick={() => removeOption(idx)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-red-500 transition-colors" title="Remove option">
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Order badge */}
                <span className="absolute -top-2 -left-1 bg-orange-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
                  {idx + 1}
                </span>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Variant Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={option.name}
                    onChange={e => updateOption(idx, "name", e.target.value)}
                    placeholder="e.g. Color, Size"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Variant Values <span className="text-red-500">*</span>
                  </label>
                  <TagInput
                    tags={option.values}
                    onChange={v => updateOption(idx, "values", v)}
                    placeholder="Type value and press Enter..."
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {variantOptions.length > 0 && (
          <label className="flex items-center gap-2 mt-4 cursor-pointer">
            <div
              onClick={() => handleAutoGenerate(!autoGenerate)}
              className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                autoGenerate ? "bg-orange-500 border-orange-500" : "border-slate-300"
              }`}
            >
              {autoGenerate && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-slate-700">Generate all combinations automatically</span>
          </label>
        )}
      </div>

      {/* Variant Inventory & Pricing */}
      {variantCombinations.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800">Variant Inventory &amp; Pricing</h3>
          </div>
          <div className="overflow-x-auto">
            <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 flex items-center gap-2">
              <span className="text-[11px] font-bold text-orange-600 uppercase tracking-wide">Default Row</span>
              <span className="text-[11px] text-orange-500">— Enter a common value and click <strong>↓ All</strong> to apply it to every variant row below.</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {variantKeys.map(k => (
                    <th key={k} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase capitalize">
                      {k}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">SKU</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Price (₹)</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">MRP (₹)</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Stock</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Weight</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Cont. Selling</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Images</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">

                {/* ── Default / Bulk-Apply Row ── */}
                <tr className="bg-orange-50 border-b-2 border-orange-200">
                  {variantKeys.map(k => (
                    <td key={k} className="px-3 py-2">
                      <span className="text-[10px] text-orange-400 font-bold uppercase">all</span>
                    </td>
                  ))}
                  {/* SKU default */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input type="text" placeholder="— SKU prefix"
                        value={defaultRow.sku}
                        onChange={e => setDefaultRow(r => ({ ...r, sku: e.target.value }))}
                        className="w-24 px-2 py-1 border border-orange-200 bg-white rounded text-xs focus:outline-none focus:border-orange-500"
                        data-testid="default-row-sku" />
                      <button type="button" title="Apply SKU to all rows"
                        onClick={() => { if (!defaultRow.sku) return; onChange("variant_combinations", variantCombinations.map(c => ({ ...c, sku: defaultRow.sku }))); }}
                        className="px-1.5 py-1 rounded bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors whitespace-nowrap">↓ All</button>
                    </div>
                  </td>
                  {/* Price default */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" placeholder="0"
                        value={defaultRow.price}
                        onChange={e => setDefaultRow(r => ({ ...r, price: e.target.value }))}
                        className="w-16 px-2 py-1 border border-orange-200 bg-white rounded text-xs focus:outline-none focus:border-orange-500"
                        data-testid="default-row-price" />
                      <button type="button" title="Apply Price to all rows"
                        onClick={() => { if (defaultRow.price === "") return; onChange("variant_combinations", variantCombinations.map(c => ({ ...c, price: Number(defaultRow.price) }))); }}
                        className="px-1.5 py-1 rounded bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors whitespace-nowrap">↓ All</button>
                    </div>
                  </td>
                  {/* MRP default */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" placeholder="0"
                        value={defaultRow.mrp}
                        onChange={e => setDefaultRow(r => ({ ...r, mrp: e.target.value }))}
                        className="w-16 px-2 py-1 border border-orange-200 bg-white rounded text-xs focus:outline-none focus:border-orange-500"
                        data-testid="default-row-mrp" />
                      <button type="button" title="Apply MRP to all rows"
                        onClick={() => { if (defaultRow.mrp === "") return; onChange("variant_combinations", variantCombinations.map(c => ({ ...c, mrp: Number(defaultRow.mrp) }))); }}
                        className="px-1.5 py-1 rounded bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors whitespace-nowrap">↓ All</button>
                    </div>
                  </td>
                  {/* Stock default */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" placeholder="0"
                        value={defaultRow.stock}
                        onChange={e => setDefaultRow(r => ({ ...r, stock: e.target.value }))}
                        className="w-16 px-2 py-1 border border-orange-200 bg-white rounded text-xs focus:outline-none focus:border-orange-500"
                        data-testid="default-row-stock" />
                      <button type="button" title="Apply Stock to all rows"
                        onClick={() => { if (defaultRow.stock === "") return; onChange("variant_combinations", variantCombinations.map(c => ({ ...c, stock: Number(defaultRow.stock) }))); }}
                        className="px-1.5 py-1 rounded bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors whitespace-nowrap">↓ All</button>
                    </div>
                  </td>
                  {/* Weight default */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" step="0.01" placeholder="0"
                        value={defaultRow.weight}
                        onChange={e => setDefaultRow(r => ({ ...r, weight: e.target.value }))}
                        className="w-16 px-2 py-1 border border-orange-200 bg-white rounded text-xs focus:outline-none focus:border-orange-500"
                        data-testid="default-row-weight" />
                      <button type="button" title="Apply Weight to all rows"
                        onClick={() => { if (defaultRow.weight === "") return; onChange("variant_combinations", variantCombinations.map(c => ({ ...c, weight: Number(defaultRow.weight) }))); }}
                        className="px-1.5 py-1 rounded bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors whitespace-nowrap">↓ All</button>
                    </div>
                  </td>
                  {/* Continue Selling default – bulk set all rows */}
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-[9px] text-orange-400 font-bold uppercase tracking-wide leading-none">Bulk set</span>
                      <div className="flex items-center gap-0.5">
                        <button type="button" title="Set all variants: Continue selling when OOS"
                          onClick={() => onChange("variant_combinations", variantCombinations.map(c => ({ ...c, continue_selling_oos: true })))}
                          data-testid="bulk-oos-yes"
                          className="px-1.5 py-1 rounded bg-green-500 text-white text-[10px] font-bold hover:bg-green-600 transition-colors">Y</button>
                        <button type="button" title="Set all variants: Stop when OOS"
                          onClick={() => onChange("variant_combinations", variantCombinations.map(c => ({ ...c, continue_selling_oos: false })))}
                          data-testid="bulk-oos-no"
                          className="px-1.5 py-1 rounded bg-red-500 text-white text-[10px] font-bold hover:bg-red-600 transition-colors">N</button>
                        <button type="button" title="Reset all to product default"
                          onClick={() => onChange("variant_combinations", variantCombinations.map(c => { const n = { ...c }; delete n.continue_selling_oos; return n; }))}
                          data-testid="bulk-oos-reset"
                          className="px-1.5 py-1 rounded bg-slate-400 text-white text-[10px] font-bold hover:bg-slate-500 transition-colors">—</button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] text-orange-500 font-bold">Default Row</span>
                  </td>
                </tr>

                {/* ── Individual variant rows ── */}
                {variantCombinations.map((combo, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    {variantKeys.map((k, kIdx) => (
                      <td key={k} className="px-3 py-2">
                        <span className="flex items-center gap-1.5">
                          {kIdx === 0 && (combo.images?.[0] || combo.image || mainImage) && (
                            <img
                              src={combo.images?.[0] || combo.image || mainImage}
                              alt={combo[k] || ""}
                              className="w-7 h-7 rounded object-cover border border-slate-200 flex-shrink-0"
                              onError={e => { e.target.style.display = "none"; }}
                            />
                          )}
                          {combo[k] || "—"}
                        </span>
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={combo.sku || ""}
                        onChange={e => updateCombination(idx, "sku", e.target.value)}
                        className="w-24 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-orange-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        value={combo.price || ""}
                        onChange={e => updateCombination(idx, "price", e.target.value)}
                        className="w-16 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-orange-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        value={combo.mrp || ""}
                        onChange={e => updateCombination(idx, "mrp", e.target.value)}
                        className="w-16 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-orange-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        value={combo.stock || ""}
                        onChange={e => updateCombination(idx, "stock", e.target.value)}
                        className="w-16 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-orange-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={combo.weight || ""}
                        onChange={e => updateCombination(idx, "weight", e.target.value)}
                        className="w-16 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-orange-500"
                      />
                    </td>
                    {/* Continue Selling per-variant toggle */}
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        data-testid={`oos-toggle-${idx}`}
                        title={
                          combo.continue_selling_oos === true  ? "Continue selling when OOS (click to change to: Stop)"
                          : combo.continue_selling_oos === false ? "Stop when OOS (click to change to: Product default)"
                          : "Use product default (click to change to: Continue)"
                        }
                        onClick={() => {
                          const cur = combo.continue_selling_oos;
                          updateCombination(idx, "continue_selling_oos",
                            cur === undefined || cur === null ? true
                            : cur === true ? false
                            : undefined
                          );
                        }}
                        className={`w-16 h-6 rounded flex items-center justify-center text-[10px] font-bold transition-colors border ${
                          combo.continue_selling_oos === true
                            ? "bg-green-50 text-green-600 border-green-300"
                            : combo.continue_selling_oos === false
                            ? "bg-red-50 text-red-500 border-red-300"
                            : "bg-slate-50 text-slate-400 border-slate-200"
                        }`}
                      >
                        {combo.continue_selling_oos === true ? "✓ Yes"
                          : combo.continue_selling_oos === false ? "✗ No"
                          : "— Def."}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <VariantImagesCell
                        combo={combo}
                        mainImage={mainImage}
                        onEdit={() => setEditImagesIdx(idx)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeCombination(idx)}
                        className="text-slate-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Variant Images Modal */}
      {editImagesIdx !== null && variantCombinations[editImagesIdx] && (
        <VariantImagesModal
          combo={variantCombinations[editImagesIdx]}
          variantLabel={getVariantLabel(variantCombinations[editImagesIdx])}
          onSave={(cleanImages) => handleImagesSave(editImagesIdx, cleanImages)}
          onClose={() => setEditImagesIdx(null)}
        />
      )}
    </div>
  );
}
